/**
 * Mutate an existing skill prompt on the user's active Prompt Lab
 * session. Partial-update shape: pass only the fields you want to
 * change.
 *
 * Use for "raise the weight on redlines", "disable the second skill",
 * "rewrite the content of skill X", etc.
 *
 * Approval-gated.
 */

import type { NipeTool } from './types';

export interface UpdateSkillPromptContext {
  getActivePromptSessionId?: () => string | null;
  updateSkillPromptOnSession?: (
    sessionId: string,
    skillPromptId: string,
    changes: {
      name?: string;
      content?: string;
      weight?: number;
      enabled?: boolean;
    },
  ) => { ok: true } | { ok: false; reason: string };
}

const MAX_CONTENT_BYTES = 50_000;
const MAX_NAME_LEN = 200;

export const updateSkillPromptTool: NipeTool<UpdateSkillPromptContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'updateSkillPrompt',
      description:
        'Mutate an existing skill prompt on the active Prompt Lab session. ' +
        'Identify by `skillPromptId`. Only the fields you pass get changed; ' +
        'omit a field to leave it alone. Read the session first via ' +
        'readPromptSession so you know the current values before partial-' +
        'updating.',
      parameters: {
        type: 'object',
        properties: {
          skillPromptId: {
            type: 'string',
            description:
              'The session-scoped id of the skill prompt to update.',
          },
          name: {
            type: 'string',
            description: 'New display name (optional).',
          },
          content: {
            type: 'string',
            description: 'New prompt content (optional).',
          },
          weight: {
            type: 'number',
            description:
              'New weight (optional). Typical range 0.0–5.0; the slice ' +
              'does not clamp but the runtime composer may.',
          },
          enabled: {
            type: 'boolean',
            description: 'Enable / disable the slot (optional).',
          },
        },
        required: ['skillPromptId'],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (!ctx.getActivePromptSessionId || !ctx.updateSkillPromptOnSession) {
      return {
        ok: false,
        reason: 'Prompt Lab is not mounted in this session.',
      };
    }
    const sessionId = ctx.getActivePromptSessionId();
    if (sessionId === null) {
      return { ok: false, reason: 'No active prompt session yet.' };
    }
    const skillPromptId =
      typeof args.skillPromptId === 'string' ? args.skillPromptId.trim() : '';
    if (skillPromptId.length === 0) {
      return { ok: false, reason: '`skillPromptId` is required.' };
    }
    const changes: {
      name?: string;
      content?: string;
      weight?: number;
      enabled?: boolean;
    } = {};
    if (typeof args.name === 'string') {
      if (args.name.length > MAX_NAME_LEN) {
        return {
          ok: false,
          reason: `name too long (${args.name.length} > ${MAX_NAME_LEN}).`,
        };
      }
      changes.name = args.name;
    }
    if (typeof args.content === 'string') {
      if (args.content.length > MAX_CONTENT_BYTES) {
        return {
          ok: false,
          reason: `content too long (${args.content.length} > ${MAX_CONTENT_BYTES}).`,
        };
      }
      changes.content = args.content;
    }
    if (typeof args.weight === 'number') changes.weight = args.weight;
    if (typeof args.enabled === 'boolean') changes.enabled = args.enabled;
    if (Object.keys(changes).length === 0) {
      return {
        ok: false,
        reason: 'No fields to update. Pass at least one of name, content, weight, enabled.',
      };
    }
    return ctx.updateSkillPromptOnSession(sessionId, skillPromptId, changes);
  },
};
